import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Natavování živic - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: vyhl. 87/2000 Sb. (podmínky požární bezpečnosti při svařování a
 * nahřívání živic v tavných nádobách - §3 covers torch-applied bitumen as a
 * požárně nebezpečná práce in its own right, distinct from welding). Shares
 * the fire-watch discipline with the welding lesson and the extinguisher
 * skills from fire-protection - this course cross-references both. Bilingual
 * (cs-CZ + en-US). Three chapters (před zapálením hořáku | plamen na střeše |
 * požární dohled) + a graded quiz with one question per kind. Answer keys
 * live here (repo-privileged) and in the DB; stripped from the client payload.
 */
export const hotWorksBitumenProfile: TrainingProfile = {
  slug: 'hot-works-bitumen',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Natavování živic',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-priprava',
        title: t('Před zapálením hořáku', 'Before lighting the torch'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-priprava',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Požárně nebezpečná práce začíná papírem', 'Fire-hazard work starts on paper'),
          },
          {
            id: 'p-priprava-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Natavování asfaltových pásů plamenem je **požárně nebezpečná práce** stejně jako svařování. Před začátkem posuď pracoviště a **odstraň nebo zakryj hořlavé materiály** v dosahu - na střeše to znamená i to, co je **pod** tebou: prostupy, spáry u atiky a světlíky vedou plamen a jiskry do konstrukce. Připrav **hasicí přístroj přímo na střechu**, ne do dodávky.',
              'Torch-applying bitumen membranes is **fire-hazard work** just like welding. Before starting, assess the workplace and **remove or cover combustible materials** within reach - on a roof that includes what is **below** you: penetrations, gaps at the parapet and skylights carry flame and sparks into the structure. Put an **extinguisher on the roof itself**, not in the van.',
            ),
          },
          {
            id: 'c-priprava-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **vyhl. 87/2000 Sb. §3** (nahřívání živic a natavování). Podmínky se stanoví předem - u zvýšeného nebezpečí formou **písemného příkazu** k práci. Hasicí přístroje a dohled nejsou volitelná výbava.',
              'Legal basis: **Decree 87/2000 Coll. §3** (heating and torch-applying bitumen). Conditions are set in advance - where the hazard is elevated, via a **written hot-work permit**. Extinguishers and the fire watch are not optional equipment.',
            ),
          },
          {
            id: 'p-priprava-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**PB lahev** stojí vždy nastojato, zajištěná proti pádu, hadice bez prasklin a s funkční **pojistkou proti zpětnému šlehnutí**. Lahev drž mimo dosah plamene a nenech ji na slunci u rozpáleného asfaltu. Tavná nádoba na živici se **nepřehřívá a nezakrývá se hořlavým víkem** - přehřátá živice vzplane sama.',
              'The **LPG cylinder** always stands upright, secured against falling, hoses crack-free with a working **flashback arrestor**. Keep the cylinder out of flame reach and out of the sun next to hot asphalt. A bitumen melting pot must **not be overheated or covered with a combustible lid** - overheated bitumen self-ignites.',
            ),
          },
          {
            id: 'cl-priprava',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Hořlavé materiály odstraněny/zakryty vč. prostupů',
                'Combustibles removed/covered incl. penetrations',
              ),
              t('Hasicí přístroj na střeše, v dosahu', 'Extinguisher on the roof, within reach'),
              t(
                'PB lahev nastojato, hadice + pojistka v pořádku',
                'LPG cylinder upright, hoses + arrestor sound',
              ),
              t('Stanovené podmínky / písemný příkaz', 'Conditions set / written hot-work permit'),
            ],
          },
        ],
      },
      {
        id: 'ch-plamen',
        title: t('Plamen na střeše', 'Flame on the roof'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-plamen'],
        blocks: [
          {
            id: 'h-plamen',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Nejhorší oheň je ten, který nevidíš',
              'The worst fire is the one you cannot see',
            ),
          },
          {
            id: 'p-plamen-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Pod hydroizolací bývá **polystyren (EPS) nebo jiná hořlavá izolace**. Plamen zavedený do **spáry, k atice nebo k prostupu** zapálí izolaci uvnitř skladby - oheň pak **doutná skrytě** i hodiny a projeví se, až když hoří konstrukce. Proto: detaily u prostupů a atik se natavují **horkovzdušně nebo se lepí**, plamen nikdy nesměřuje do dutin.',
              'Beneath the waterproofing there is usually **polystyrene (EPS) or other combustible insulation**. A flame led into a **gap, parapet joint or penetration** ignites the insulation inside the build-up - the fire then **smoulders hidden** for hours and shows itself only once the structure burns. Therefore: details at penetrations and parapets are done with **hot air or adhesive**, and the flame never points into cavities.',
            ),
          },
          {
            id: 'anim-plamen',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'bitumenTorch',
            steps: [
              {
                label: t('Plamen ve spáře', 'Flame in the joint'),
                say: t('Izolace skrytě doutná.', 'The insulation smoulders unseen.'),
              },
              {
                label: t('Plamen jen na volnou plochu', 'Flame only on the open membrane'),
                say: t(
                  'Hasicí přístroj na střeše, dohled po skončení.',
                  'Extinguisher on the roof, fire watch afterwards.',
                ),
              },
            ],
            caption: t(
              'Plamen do spáry u atiky = skrytě doutnající izolace. Detaily horkovzdušně, plamen jen na volnou plochu pásu.',
              'Flame into a parapet gap = insulation smouldering out of sight. Details with hot air; the torch only on the open membrane surface.',
            ),
          },
          {
            id: 'p-plamen-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Roztavená živice má přes **200 °C** a na kůži se lepí - pracuj v rukavicích, dlouhém rukávu a s nohavicemi přes obuv, ne zastrčenými do ní. Hořák odkládej **jen zhasnutý** na nehořlavou podložku; zapálený hořák nikdy nenechávej bez dozoru, ani „na chvilku".',
              'Molten bitumen exceeds **200 °C** and sticks to skin - work in gloves, long sleeves and trouser legs over the boots, not tucked in. Put the torch down **only extinguished**, on a non-combustible pad; never leave a lit torch unattended, not even "for a moment".',
            ),
          },
          {
            id: 'kc-plamen',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Jak se řeší detail u prostupu střechou?',
              'How is a detail at a roof penetration handled?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Kratším plamenem přímo do spáry',
                  'With a shorter flame directly into the gap',
                ),
              },
              {
                id: 'kc-b',
                label: t(
                  'Horkovzdušně nebo lepením - plamen do dutin nesmí',
                  'With hot air or adhesive - no flame into cavities',
                ),
              },
              {
                id: 'kc-c',
                label: t('Plamenem z větší vzdálenosti', 'With the flame from a greater distance'),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Plamen ve spáře zapálí izolaci uvnitř skladby, kde doutná skrytě. Detaily patří horkému vzduchu nebo lepidlu.',
              'A flame in the gap ignites insulation inside the build-up, where it smoulders unseen. Details belong to hot air or adhesive.',
            ),
          },
        ],
      },
      {
        id: 'ch-dohled',
        title: t('Požární dohled', 'Fire watch'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-ukonceni'],
        blocks: [
          {
            id: 'h-dohled',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Práce nekončí zhasnutím hořáku',
              'The job does not end when the torch goes out',
            ),
          },
          {
            id: 'p-dohled-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Skrytě doutnající izolace se projeví typicky **za několik hodin** - proto po skončení natavování pracoviště **kontroluje pověřená osoba nejméně po dobu 8 hodin** (interval kontrol stanoví podmínky práce). Dohled se drží i **během přestávek**. Stejná disciplína platí pro svařování - detaily máš v lekci **Svařování a řezání** a hašení v lekci **Požární ochrana**.',
              'Hidden smouldering insulation typically reveals itself **hours later** - so after torching ends, a designated person **checks the workplace for at least 8 hours** (the check interval is set by the work conditions). The watch is kept **during breaks** too. The same discipline applies to welding - details are in the **Welding and cutting** lesson and extinguishing in **Fire protection**.',
            ),
          },
          {
            id: 'c-dohled-varovani',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Většina požárů střech od natavování vznikne **až po odchodu party**. Odjezd v 16:00 bez dohledu = požár ve 21:00 bez svědků.',
              'Most roof fires from torching start **after the crew has left**. Leaving at 4 p.m. with no watch = a fire at 9 p.m. with no witnesses.',
            ),
          },
          {
            id: 'ord-ukonceni',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď správné ukončení natavování.',
              'Order the correct end of a torching shift.',
            ),
            items: [
              {
                id: 'ou-1',
                label: t(
                  'Zhasnutí hořáku a uzavření lahve',
                  'Extinguish the torch and close the cylinder',
                ),
              },
              {
                id: 'ou-2',
                label: t(
                  'Prohlídka pracoviště vč. detailů a dutin',
                  'Inspect the workplace incl. details and cavities',
                ),
              },
              {
                id: 'ou-3',
                label: t(
                  'Předání pracoviště požárnímu dohledu',
                  'Hand the workplace over to the fire watch',
                ),
              },
              {
                id: 'ou-4',
                label: t(
                  'Kontroly po stanovenou dobu (min. 8 h)',
                  'Checks for the set period (min. 8 h)',
                ),
              },
            ],
          },
          {
            id: 'k-dohled',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Plamen jen na volnou plochu, detaily horkovzdušně, hasicí přístroj na střeše.** Po skončení: prohlídka a dohled nejméně 8 hodin - oheň od živice chodí pozdě.',
              '**Flame only on the open surface, details with hot air, extinguisher on the roof.** Afterwards: inspection and a watch of at least 8 hours - bitumen fires arrive late.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-detail-single',
      kind: 'single',
      chapterId: 'ch-plamen',
      prompt: t('Kam plamen hořáku nikdy nesmí?', 'Where must the torch flame never go?'),
      choices: [
        {
          id: 'a',
          label: t(
            'Do spár, dutin a prostupů ve skladbě střechy',
            'Into gaps, cavities and penetrations in the roof build-up',
          ),
        },
        {
          id: 'b',
          label: t('Na volnou plochu asfaltového pásu', 'Onto the open surface of the membrane'),
        },
        { id: 'c', label: t('Na přesah pásu při spoji', 'Onto the membrane overlap at a seam') },
      ],
      correct: 'a',
    },
    {
      id: 'q-dohled-tf',
      kind: 'truefalse',
      chapterId: 'ch-dohled',
      prompt: t(
        'Po zhasnutí hořáku a úklidu nářadí může celá parta hned odjet.',
        'Once the torch is out and the tools packed, the whole crew can leave immediately.',
      ),
      correct: false,
    },
    {
      id: 'q-priprava-multi',
      kind: 'multi',
      chapterId: 'ch-priprava',
      prompt: t(
        'Co musí být připraveno před zapálením hořáku? (vyber všechny správné)',
        'What must be in place before lighting the torch? (select all that apply)',
      ),
      choices: [
        {
          id: 'a',
          label: t('Hasicí přístroj přímo na střeše', 'An extinguisher on the roof itself'),
        },
        {
          id: 'b',
          label: t(
            'Odstraněné/zakryté hořlaviny vč. prostupů',
            'Combustibles removed/covered incl. penetrations',
          ),
        },
        {
          id: 'c',
          label: t(
            'PB lahev naležato ve stínu pásu',
            "The LPG cylinder lying down in the membrane's shade",
          ),
        },
        {
          id: 'd',
          label: t(
            'Stanovené podmínky, případně písemný příkaz',
            'Set conditions, or a written hot-work permit',
          ),
        },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-dohled-numeric',
      kind: 'numeric',
      chapterId: 'ch-dohled',
      prompt: t(
        'Nejméně kolik hodin po skončení natavování musí trvat požární dohled?',
        'For at least how many hours after torching ends must the fire watch continue?',
      ),
      correct: 8,
      tolerance: 0,
    },
    {
      id: 'q-ukonceni-order',
      kind: 'ordering',
      chapterId: 'ch-dohled',
      prompt: t('Seřaď ukončení natavování.', 'Order the end of a torching shift.'),
      choices: [
        { id: 'a', label: t('Zhasnout hořák, zavřít lahev', 'Torch out, cylinder closed') },
        { id: 'b', label: t('Prohlídka pracoviště', 'Workplace inspection') },
        { id: 'c', label: t('Předání dohledu', 'Handover to the fire watch') },
        { id: 'd', label: t('Kontroly min. 8 hodin', 'Checks for min. 8 hours') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
